// Voter portal — mobile-first ballot UI. The single most important UX in the
// product: residents will judge the platform almost entirely by this page.
//
// Loaded with a single-use ballot token in the URL. Renders the election,
// the proposed action verbatim, the deadline, and the questions. Supports
// all four ballot types: yes_no_abstain, single_choice, multi_choice,
// ranked_choice (drag to reorder).

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';

interface Question {
  id: string;
  position: number;
  question_text: string;
  question_type: 'yes_no_abstain' | 'single_choice' | 'multi_choice' | 'ranked_choice';
  min_selections: number;
  max_selections: number;
}

interface Option { id: string; question_id: string; position: number; option_text: string }

interface Ballot {
  election: { id: string; title: string; description: string | null; proposedActionText: string; opensAt: number; closesAt: number; status: string; quorumPct: number; passingThreshold: string; customPctValue: number | null };
  tenant: { name: string; brandColor: string | null };
  voter: { name: string; email: string; lot: { number: string; address: string } };
  alreadyVoted: boolean;
  questions: Question[];
  options: Option[];
}

const DEFAULT_BRAND = '#10b981';

export function Voter() {
  const { token = '' } = useParams();
  const [ballot, setBallot] = useState<Ballot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ contentHash: string; castAt: number } | null>(null);
  const [responses, setResponses] = useState<Record<string, { selected: string[]; rankedOrder: string[] }>>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ ballot: Ballot }>(`/voter/ballot/${encodeURIComponent(token)}`);
        setBallot(res.ballot);
        const init: Record<string, { selected: string[]; rankedOrder: string[] }> = {};
        for (const q of res.ballot.questions) init[q.id] = { selected: [], rankedOrder: [] };
        setResponses(init);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load ballot');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const optionsByQuestion = useMemo(() => {
    const m = new Map<string, Option[]>();
    for (const o of ballot?.options ?? []) {
      let arr = m.get(o.question_id);
      if (!arr) { arr = []; m.set(o.question_id, arr); }
      arr.push(o);
    }
    return m;
  }, [ballot]);

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center gap-3 text-ink-500">
          <div className="w-5 h-5 border-2 border-ink-200 border-t-brand-500 rounded-full animate-spin" />
          Loading your ballot…
        </div>
      </Wrap>
    );
  }
  if (error) return <Wrap><h1 className="text-xl font-semibold text-red-700">Cannot load ballot</h1><p className="mt-2 text-ink-600">{error}</p></Wrap>;
  if (!ballot) return null;

  if (done) {
    return (
      <Wrap brandColor={ballot.tenant.brandColor}>
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-brand-50 text-brand-600 grid place-items-center mx-auto mb-5">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Vote recorded.</h1>
          <p className="mt-3 text-ink-600">Thanks, {ballot.voter.name}. Your ballot was recorded at <span className="text-ink-900">{new Date(done.castAt).toLocaleString()}</span>.</p>
          <p className="mt-4 text-xs text-ink-500">
            Receipt hash: <code className="font-mono bg-ink-100 px-2 py-0.5 rounded text-ink-700">{done.contentHash.slice(0, 16)}…</code>
          </p>
        </div>
      </Wrap>
    );
  }
  if (ballot.alreadyVoted) {
    return (
      <Wrap brandColor={ballot.tenant.brandColor}>
        <h1 className="text-xl font-semibold tracking-tight">You've already voted.</h1>
        <p className="mt-2 text-ink-600">Each ballot can only be cast once. If you believe this is wrong, contact your board.</p>
      </Wrap>
    );
  }
  if (ballot.election.status !== 'open') {
    return (
      <Wrap brandColor={ballot.tenant.brandColor}>
        <h1 className="text-xl font-semibold tracking-tight">Voting is not currently open.</h1>
        <p className="mt-2 text-ink-600">This election is in status: <strong className="text-ink-900">{ballot.election.status}</strong>.</p>
      </Wrap>
    );
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const items = ballot!.questions.map((q) => {
        const r = responses[q.id]!;
        if (q.question_type === 'ranked_choice') return { questionId: q.id, rankedOrder: r.rankedOrder };
        return { questionId: q.id, selectedOptionIds: r.selected };
      });
      for (const q of ballot!.questions) {
        const r = responses[q.id]!;
        if (q.question_type === 'ranked_choice') {
          if (r.rankedOrder.length === 0) throw new Error(`Please rank at least one option for: ${q.question_text}`);
        } else if (r.selected.length < q.min_selections || r.selected.length > q.max_selections) {
          throw new Error(`Question "${q.question_text}" requires between ${q.min_selections} and ${q.max_selections} selections`);
        }
      }
      const res = await api.post<{ ok: boolean; voteId: string; castAt: number; contentHash: string }>(`/voter/ballot/${encodeURIComponent(token)}/cast`, { responses: items });
      setDone({ castAt: res.castAt, contentHash: res.contentHash });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'already_voted') {
        setError('This ballot has already been cast.');
      } else {
        setError(err instanceof Error ? err.message : 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const closesAt = new Date(ballot.election.closesAt);

  return (
    <Wrap brandColor={ballot.tenant.brandColor}>
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{ballot.tenant.name}</div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{ballot.election.title}</h1>
      <p className="mt-1 text-sm text-ink-600">Voting closes <strong className="text-ink-900">{closesAt.toLocaleString()}</strong></p>

      <section className="mt-7 p-5 border-l-4 border-brand-500 bg-brand-50/40 rounded-r-lg">
        <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Proposed action</div>
        <p className="mt-1.5 whitespace-pre-wrap text-ink-900">{ballot.election.proposedActionText}</p>
      </section>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs text-ink-600">
        <Stat label="Quorum required" value={`${ballot.election.quorumPct}%`} />
        <Stat label="Passing threshold" value={describeThreshold(ballot.election.passingThreshold, ballot.election.customPctValue)} />
        <Stat label="Voting as" value={`${ballot.voter.name}, Lot ${ballot.voter.lot.number}`} />
      </div>

      <div className="mt-10 space-y-10">
        {ballot.questions.map((q, i) => (
          <QuestionView
            key={q.id}
            index={i}
            question={q}
            options={optionsByQuestion.get(q.id) ?? []}
            response={responses[q.id]!}
            onChange={(r) => setResponses({ ...responses, [q.id]: r })}
          />
        ))}
      </div>

      {error && <div className="mt-5 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}

      <button
        onClick={submit}
        disabled={submitting}
        className="mt-8 w-full py-3.5 bg-ink-900 text-white font-semibold rounded-lg hover:bg-ink-800 transition disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Cast my ballot'}
      </button>
      <p className="mt-3 text-xs text-ink-500 text-center">You can only submit this ballot once. Review your selections before clicking.</p>
    </Wrap>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-400 font-medium">{label}</div>
      <div className="mt-0.5 text-ink-900 font-medium">{value}</div>
    </div>
  );
}

function QuestionView({ index, question, options, response, onChange }: { index: number; question: Question; options: Option[]; response: { selected: string[]; rankedOrder: string[] }; onChange: (r: { selected: string[]; rankedOrder: string[] }) => void }) {
  const heading = (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-400 font-medium">Question {index + 1}</div>
      <h2 className="mt-1 font-semibold text-lg text-ink-900">{question.question_text}</h2>
    </div>
  );

  if (question.question_type === 'yes_no_abstain' || question.question_type === 'single_choice') {
    return (
      <div>
        {heading}
        <div className="mt-4 space-y-2">
          {options.map((o) => {
            const selected = response.selected[0] === o.id;
            return (
              <label
                key={o.id}
                className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition ${
                  selected ? 'border-brand-500 bg-brand-50/50 ring-2 ring-brand-500/20' : 'border-ink-200 hover:border-ink-300 bg-white'
                }`}
              >
                <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${selected ? 'border-brand-500' : 'border-ink-300'}`}>
                  {selected && <span className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
                </span>
                <input type="radio" name={question.id} className="sr-only" checked={selected} onChange={() => onChange({ selected: [o.id], rankedOrder: [] })} />
                <span className="text-ink-900">{o.option_text}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (question.question_type === 'multi_choice') {
    return (
      <div>
        {heading}
        <p className="text-sm text-ink-500 mt-1">Select between {question.min_selections} and {question.max_selections}.</p>
        <div className="mt-4 space-y-2">
          {options.map((o) => {
            const checked = response.selected.includes(o.id);
            return (
              <label
                key={o.id}
                className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition ${
                  checked ? 'border-brand-500 bg-brand-50/50 ring-2 ring-brand-500/20' : 'border-ink-200 hover:border-ink-300 bg-white'
                }`}
              >
                <span className={`w-5 h-5 rounded-md border-2 flex-shrink-0 grid place-items-center ${checked ? 'border-brand-500 bg-brand-500' : 'border-ink-300'}`}>
                  {checked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                </span>
                <input type="checkbox" className="sr-only" checked={checked}
                  onChange={() => {
                    const sel = checked ? response.selected.filter((id) => id !== o.id) : [...response.selected, o.id];
                    if (sel.length > question.max_selections) return;
                    onChange({ selected: sel, rankedOrder: [] });
                  }} />
                <span className="text-ink-900">{o.option_text}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // ranked_choice
  return (
    <div>
      {heading}
      <p className="text-sm text-ink-500 mt-1">Tap options in your order of preference. Tap again to remove.</p>
      <div className="mt-4 space-y-2">
        {options.map((o) => {
          const rank = response.rankedOrder.indexOf(o.id);
          return (
            <button type="button" key={o.id}
              onClick={() => {
                if (rank >= 0) {
                  const next = response.rankedOrder.filter((id) => id !== o.id);
                  onChange({ selected: [], rankedOrder: next });
                } else {
                  onChange({ selected: [], rankedOrder: [...response.rankedOrder, o.id] });
                }
              }}
              className={`w-full text-left flex items-center gap-3 p-4 border rounded-xl transition ${
                rank >= 0 ? 'border-brand-500 bg-brand-50/50 ring-2 ring-brand-500/20' : 'border-ink-200 hover:border-ink-300 bg-white'
              }`}
            >
              <span className={`w-8 h-8 rounded-full grid place-items-center text-sm font-semibold flex-shrink-0 ${
                rank >= 0 ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-500'
              }`}>
                {rank >= 0 ? rank + 1 : '·'}
              </span>
              <span className="text-ink-900">{o.option_text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Wrap({ children, brandColor }: { children: React.ReactNode; brandColor?: string | null }) {
  return (
    <div className="min-h-screen bg-ink-50">
      <div className="max-w-2xl mx-auto p-4 sm:p-10">
        <div className="bg-white rounded-2xl shadow-card border-t-[5px] p-6 sm:p-10" style={{ borderColor: brandColor ?? DEFAULT_BRAND }}>
          {children}
        </div>
        <p className="text-center text-xs text-ink-400 mt-5">
          Powered by <span className="font-medium text-ink-600">HOA-Vote</span> · La. R.S. 9:1141.28(B): vote conducted without a meeting
        </p>
      </div>
    </div>
  );
}

function describeThreshold(t: string, custom: number | null): string {
  if (t === 'majority') return 'majority';
  if (t === 'two_thirds') return 'two-thirds';
  if (t === 'three_fourths') return 'three-fourths';
  return `${custom ?? '?'}%`;
}
